/**
 * 표 **행 높이(row height)** 드래그 리사이즈 ProseMirror 플러그인.
 *
 * prosemirror-tables의 `columnResizing`(열 너비)을 **수직축으로 미러링**한 구현이다.
 * columnResizing이 없는 행 버전을 동일한 구조로 직접 작성한다:
 *
 *  - 아무 행의 **아래 경계**(또는 위 경계)에 hover → `row-resize` 커서 + 핸들 노출
 *  - 경계를 드래그 → 대상 행 높이를 **실시간 프리뷰**(셀에 height 데코레이션)
 *  - 드롭 → 해당 행의 모든 셀에 `rowHeight` 노드 attr을 `setNodeMarkup`으로 일괄 커밋
 *
 * 저장은 셀 단위 `rowHeight` 속성(RowHeightExtension의 글로벌 attribute)으로 이뤄지며,
 * 이는 BlockNote가 행 props를 모델링하지 않기 때문이다(행 높이 = 행 셀들의 max height).
 *
 * 라이브 프리뷰 방식(중요):
 *  열 리사이즈는 `<colgroup>`을 직접 조작해 프리뷰한다(BlockNoteTableView.ignoreMutation이
 *  colgroup 변경을 무시하므로 PM이 되돌리지 않음). 행에는 colgroup 등가물이 없고, 셀/행의
 *  style을 직접 바꾸면 PM의 MutationObserver가 노드를 즉시 다시 그려 초기화한다.
 *  → 그래서 드래그 중 높이를 **Decoration.node({style:'height:Npx'})**로 입힌다(PM이 직접
 *    렌더하므로 mutation 충돌 없음). 높이 갱신은 **meta-only 트랜잭션**으로 하여 doc을 바꾸지
 *    않으므로 onContentChange/undo 스팸이 없다. 드롭 시 한 번만 실제 커밋(단일 undo 스텝).
 *
 * ⚠️ prosemirror-state/view/tables는 반드시 에디터(@blocknote)와 **동일한 인스턴스**를
 * 써야 한다(Decoration/Plugin instanceof). 따라서 raw `prosemirror-*`에서 import하고
 * tsup 빌드에서 external 처리한다(@blocknote가 같은 패키지를 런타임 의존성으로 쓰므로
 * 소비자 트리에서 단일 복사본으로 dedup된다).
 */

import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { EditorView } from "prosemirror-view";
import { TableMap, cellAround, pointsAtCell } from "prosemirror-tables";
import {
  ROW_RESIZE_HANDLE_WIDTH,
  ROW_RESIZE_MIN_HEIGHT,
} from "../constants/limits";

export const rowResizingPluginKey = new PluginKey<RowResizeState>(
  "lumirRowResizing",
);

type Dragging = {
  startY: number;
  startHeight: number;
  /** 현재 드래그 위치 기준 프리뷰 높이(px). 데코레이션이 이 값을 렌더한다. */
  currentHeight: number;
};

/**
 * 플러그인 상태. activeHandle = 리사이즈 대상 셀의 pos(없으면 -1),
 * dragging = 드래그 중이면 시작 좌표/높이/현재 프리뷰 높이, 아니면 null.
 */
export class RowResizeState {
  constructor(
    public activeHandle: number,
    public dragging: Dragging | null,
  ) {}

  apply(tr: any): RowResizeState {
    const action = tr.getMeta(rowResizingPluginKey);
    if (action && action.setHandle != null) {
      return new RowResizeState(action.setHandle, null);
    }
    if (action && action.setDragging !== undefined) {
      return new RowResizeState(this.activeHandle, action.setDragging);
    }
    if (this.activeHandle > -1 && tr.docChanged) {
      let handle = tr.mapping.map(this.activeHandle, -1);
      if (!pointsAtCell(tr.doc.resolve(handle))) {
        handle = -1;
      }
      return new RowResizeState(handle, this.dragging);
    }
    return this;
  }
}

export function rowResizing({
  handleWidth = ROW_RESIZE_HANDLE_WIDTH,
  minHeight = ROW_RESIZE_MIN_HEIGHT,
}: { handleWidth?: number; minHeight?: number } = {}): Plugin {
  return new Plugin<RowResizeState>({
    key: rowResizingPluginKey,
    state: {
      init() {
        return new RowResizeState(-1, null);
      },
      apply(tr, prev) {
        return prev.apply(tr);
      },
    },
    props: {
      attributes: (state): Record<string, string> => {
        const pluginState = rowResizingPluginKey.getState(state);
        return pluginState && pluginState.activeHandle > -1
          ? { class: "row-resize-cursor" }
          : {};
      },
      handleDOMEvents: {
        mousemove: (view, event) => {
          handleMouseMove(view, event as MouseEvent, handleWidth);
        },
        mouseleave: (view) => {
          handleMouseLeave(view);
        },
        mousedown: (view, event) => {
          handleMouseDown(view, event as MouseEvent, minHeight);
        },
      },
      decorations: (state) => {
        const pluginState = rowResizingPluginKey.getState(state);
        if (pluginState && pluginState.activeHandle > -1) {
          return handleDecorations(state, pluginState);
        }
        return undefined;
      },
    },
  });
}

function handleMouseMove(
  view: EditorView,
  event: MouseEvent,
  handleWidth: number,
) {
  if (!view.editable) {
    return;
  }
  const pluginState = rowResizingPluginKey.getState(view.state);
  if (!pluginState) {
    return;
  }
  if (!pluginState.dragging) {
    const target = domCellAround(event.target as Node | null);
    let cell = -1;
    if (target) {
      const { top, bottom } = (target as HTMLElement).getBoundingClientRect();
      if (event.clientY - top <= handleWidth) {
        cell = edgeCell(view, event, "top", handleWidth);
      } else if (bottom - event.clientY <= handleWidth) {
        cell = edgeCell(view, event, "bottom", handleWidth);
      }
    }
    if (cell !== pluginState.activeHandle) {
      updateHandle(view, cell);
    }
  }
}

function handleMouseLeave(view: EditorView) {
  if (!view.editable) {
    return;
  }
  const pluginState = rowResizingPluginKey.getState(view.state);
  if (pluginState && pluginState.activeHandle > -1 && !pluginState.dragging) {
    updateHandle(view, -1);
  }
}

function handleMouseDown(
  view: EditorView,
  event: MouseEvent,
  minHeight: number,
): boolean {
  if (!view.editable) {
    return false;
  }
  const win = view.dom.ownerDocument.defaultView ?? window;
  const pluginState = rowResizingPluginKey.getState(view.state);
  if (!pluginState || pluginState.activeHandle === -1 || pluginState.dragging) {
    return false;
  }

  const startHeight = currentRowHeight(view, pluginState.activeHandle);
  setDragging(view, {
    startY: event.clientY,
    startHeight,
    currentHeight: startHeight,
  });

  function finish(finishEvent: MouseEvent) {
    win.removeEventListener("mouseup", finish);
    win.removeEventListener("mousemove", move);
    const ps = rowResizingPluginKey.getState(view.state);
    if (ps?.dragging) {
      const finalHeight = draggedHeight(ps.dragging, finishEvent, minHeight);
      // ⚠️ 순서 중요: 프리뷰 데코(셀 style:height)를 먼저 제거한 뒤 커밋한다.
      // 데코의 inline style과 커밋 후 renderHTML의 style이 같은 style 속성을 두고
      // 충돌하는데, 커밋 후 데코를 제거하면 PM이 style을 비우고 renderHTML 값을
      // 복원하지 않는다(빈 style). 데코를 먼저 없애면 커밋 시 renderHTML이 깨끗이
      // 적용된다. 두 dispatch는 동기로 연달아 일어나 중간 페인트가 없어 깜빡임도 없다.
      setDragging(view, null);
      commitRowHeight(view, ps.activeHandle, finalHeight);
    }
  }

  function move(moveEvent: MouseEvent) {
    // 버튼이 떼어진 채 들어온 mousemove면(놓친 mouseup 안전장치) 즉시 마무리.
    // 표준 `buttons`(0=눌림 없음)를 우선 보고, 레거시 `which`를 폴백으로 본다.
    if (
      moveEvent.buttons === 0 ||
      (moveEvent.buttons === undefined && !moveEvent.which)
    ) {
      return finish(moveEvent);
    }
    const ps = rowResizingPluginKey.getState(view.state);
    if (!ps?.dragging) {
      return;
    }
    const h = draggedHeight(ps.dragging, moveEvent, minHeight);
    if (h !== ps.dragging.currentHeight) {
      // doc은 바꾸지 않고 프리뷰 높이만 갱신(meta-only) → 데코레이션이 즉시 반영.
      setDragging(view, { ...ps.dragging, currentHeight: h });
    }
  }

  win.addEventListener("mouseup", finish);
  win.addEventListener("mousemove", move);
  event.preventDefault();
  return true;
}

/** 드래그 상태를 갱신하는 meta-only 트랜잭션(doc 미변경). */
function setDragging(view: EditorView, dragging: Dragging | null) {
  view.dispatch(
    view.state.tr.setMeta(rowResizingPluginKey, { setDragging: dragging }),
  );
}

/** 렌더된 `<tr>` 높이를 드래그 시작 높이로 쓴다(미설정 행도 자연스러운 시작점). */
function currentRowHeight(view: EditorView, cellPos: number): number {
  const info = targetRowInfo(view, cellPos);
  const tr = rowTrElement(view, cellPos, info.row);
  return tr ? tr.offsetHeight : ROW_RESIZE_MIN_HEIGHT;
}

/** 드롭 시 커밋: 대상 행에서 **끝나는** 모든 셀에 rowHeight attr 일괄 적용(기록 O). */
function commitRowHeight(view: EditorView, cellPos: number, height: number) {
  const { table, map, start, row } = targetRowInfo(view, cellPos);
  const tr = view.state.tr;
  const seen = new Set<number>();
  for (let col = 0; col < map.width; col++) {
    const cellRelPos = map.map[row * map.width + col];
    if (seen.has(cellRelPos)) {
      continue; // colspan: 같은 셀 중복 스킵
    }
    seen.add(cellRelPos);
    const rect = map.findCell(cellRelPos);
    // rowspan 셀이 이 행에서 끝나지 않으면(아래로 더 뻗으면) origin 행 높이를 유지
    if (rect.bottom - 1 !== row) {
      continue;
    }
    const node = table.nodeAt(cellRelPos);
    if (!node || node.attrs.rowHeight === height) {
      continue;
    }
    tr.setNodeMarkup(start + cellRelPos, undefined, {
      ...node.attrs,
      rowHeight: height,
    });
  }
  if (tr.docChanged) {
    view.dispatch(tr);
  }
}

/** activeHandle 셀이 속한 테이블/맵/대상 행 인덱스(이 셀의 아래 경계가 위치한 행)를 구한다. */
function targetRowInfo(view: EditorView, cellPos: number) {
  const $cell = view.state.doc.resolve(cellPos);
  const table = $cell.node(-1);
  const map = TableMap.get(table);
  const start = $cell.start(-1);
  const rect = map.findCell($cell.pos - start);
  return { table, map, start, row: rect.bottom - 1, $cell };
}

/** activeHandle 셀이 속한 `<table>`을 찾아 그 안의 row번째 `<tr>`을 반환. */
function rowTrElement(
  view: EditorView,
  cellPos: number,
  row: number,
): HTMLTableRowElement | null {
  let dom: Node | null = view.nodeDOM(cellPos) as Node | null;
  while (dom && dom.nodeName !== "TABLE") {
    dom = dom.parentNode;
  }
  if (!dom) {
    return null;
  }
  return (dom as HTMLTableElement).rows[row] ?? null;
}

function domCellAround(target: Node | null): HTMLElement | null {
  let node: any = target;
  while (node && node.nodeName !== "TD" && node.nodeName !== "TH") {
    node = node.classList?.contains("ProseMirror") ? null : node.parentNode;
  }
  return node as HTMLElement | null;
}

/**
 * 경계에 인접한 셀 pos를 구한다.
 * - "bottom": 커서가 닿은 셀(이 셀의 아래 경계를 드래그)
 * - "top":    윗 행의 셀(그 셀의 아래 경계 = 이 경계). map index - width.
 */
function edgeCell(
  view: EditorView,
  event: MouseEvent,
  side: "top" | "bottom",
  handleWidth: number,
): number {
  const offset = side === "bottom" ? -handleWidth : handleWidth;
  const found = view.posAtCoords({
    left: event.clientX,
    top: event.clientY + offset,
  });
  if (!found) {
    return -1;
  }
  const $cell = cellAround(view.state.doc.resolve(found.pos));
  if (!$cell) {
    return -1;
  }
  if (side === "bottom") {
    return $cell.pos;
  }
  const map = TableMap.get($cell.node(-1));
  const start = $cell.start(-1);
  const index = map.map.indexOf($cell.pos - start);
  return index < map.width ? -1 : start + map.map[index - map.width];
}

function updateHandle(view: EditorView, value: number) {
  view.dispatch(
    view.state.tr.setMeta(rowResizingPluginKey, { setHandle: value }),
  );
}

function draggedHeight(
  dragging: Dragging,
  event: MouseEvent,
  minHeight: number,
): number {
  const offset = event.clientY - dragging.startY;
  return Math.max(minHeight, dragging.startHeight + offset);
}

/**
 * activeHandle이 가리키는 행의 각 셀에 데코레이션을 그린다.
 *  - 하단 경계 핸들 위젯(.row-resize-handle)
 *  - 드래그 중이면: 셀에 height 인라인 style 데코레이션(라이브 프리뷰) + dragging 클래스
 */
function handleDecorations(
  state: EditorState,
  pluginState: RowResizeState,
): DecorationSet {
  const decorations: Decoration[] = [];
  const $cell = state.doc.resolve(pluginState.activeHandle);
  const table = $cell.node(-1);
  if (!table) {
    return DecorationSet.empty;
  }
  const map = TableMap.get(table);
  const start = $cell.start(-1);
  const row = map.findCell($cell.pos - start).bottom - 1;
  const dragging = pluginState.dragging;
  const seen = new Set<number>();

  for (let col = 0; col < map.width; col++) {
    const cellRelPos = map.map[row * map.width + col];
    if (seen.has(cellRelPos)) {
      continue;
    }
    seen.add(cellRelPos);
    if (map.findCell(cellRelPos).bottom - 1 !== row) {
      continue;
    }
    const node = table.nodeAt(cellRelPos);
    if (!node) {
      continue;
    }
    const from = start + cellRelPos;
    const to = from + node.nodeSize;

    if (dragging) {
      // 라이브 프리뷰: 셀에 height를 입혀 마우스를 따라 즉시 커진다(PM이 직접 렌더).
      decorations.push(
        Decoration.node(from, to, {
          class: "row-resize-dragging",
          style: `height: ${dragging.currentHeight}px`,
        }),
      );
    }

    const handle = document.createElement("div");
    handle.className = "row-resize-handle";
    decorations.push(Decoration.widget(to - 1, handle));
  }

  return DecorationSet.create(state.doc, decorations);
}
